import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, type RequestRow } from "../lib/api";
import { fmtAgo, fmtDateTime } from "../lib/format";

const STATUS_ORDER = ["doing", "verifying", "claimed", "open", "failed", "done"] as const;
const STATUS_HINT: Record<string, string> = {
  open: "waiting for an agent to claim",
  claimed: "an agent owns it, work not started",
  doing: "being implemented (worktree)",
  verifying: "runner is checking",
  done: "runner verdict green",
  failed: "runner verdict red — reopen to retry",
};

export function Requests() {
  const qc = useQueryClient();
  const requests = useQuery({ queryKey: ["requests"], queryFn: api.requests, refetchInterval: 10_000 });
  const [error, setError] = useState<string | null>(null);
  const onError = (e: unknown) => setError(e instanceof Error ? e.message : String(e));
  const onSuccess = () => {
    setError(null);
    qc.invalidateQueries({ queryKey: ["requests"] });
  };
  const create = useMutation({ mutationFn: api.requestCreate, onSuccess, onError });
  const reopen = useMutation({ mutationFn: (id: number) => api.requestUpdate(id, { status: "open" }), onSuccess, onError });
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");

  const rows = requests.data ?? [];
  const by = (s: string) => rows.filter((r) => r.status === s);

  return (
    <div className="page">
      <div className="page-head">
        <h2>Feature requests</h2>
        <span className="muted">{rows.length} total · submit here, agents claim via API, the runner's verdict closes</span>
      </div>
      {error && (
        <div className="error-bar" role="alert">
          {error}
          <button className="small" style={{ marginLeft: 12 }} onClick={() => setError(null)}>dismiss</button>
        </div>
      )}

      <div className="panel">
        <h3>New request</h3>
        <div className="row" style={{ alignItems: "flex-start", gap: 8 }}>
          <input style={{ flex: 1 }} value={title} placeholder="what should exist?" onChange={(e) => setTitle(e.target.value)} />
          <input style={{ flex: 2 }} value={note} placeholder="context, acceptance idea (optional)" onChange={(e) => setNote(e.target.value)} />
          <button
            className="primary"
            disabled={!title.trim() || create.isPending}
            onClick={() => {
              create.mutate({ title: title.trim(), note: note.trim() || undefined });
              setTitle("");
              setNote("");
            }}
          >
            submit
          </button>
        </div>
      </div>

      {STATUS_ORDER.map((s) =>
        by(s).length === 0 ? null : (
          <div className="panel" key={s}>
            <h3>
              <span className={`req-chip req-${s}`}>{s}</span> <span className="muted">{STATUS_HINT[s]}</span>
            </h3>
            {by(s).map((r) => (
              <div className="request-row" key={r.id}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div>
                    <b>#{r.id}</b> {r.title}
                  </div>
                  {r.note && <div className="muted" style={{ fontSize: 12 }}>{r.note}</div>}
                  <div className="muted" style={{ fontSize: 11 }}>
                    {r.claimant && <>by <b>{r.claimant}</b>{r.branch ? <> on <code>{r.branch}</code></> : null} · </>}
                    {r.commit_sha && <><code>{r.commit_sha}</code> · </>}
                    <span title={fmtDateTime(r.updated_ts)}>{fmtAgo(r.updated_ts)}</span>
                  </div>
                  {r.verdict && (
                    <details>
                      <summary className="muted" style={{ fontSize: 11, cursor: "pointer" }}>
                        verdict · {r.verdict_ts ? fmtAgo(r.verdict_ts) : ""}
                      </summary>
                      <pre className="block" style={{ fontSize: 11 }}>{JSON.stringify(JSON.parse(r.verdict), null, 2)}</pre>
                    </details>
                  )}
                </div>
                {r.status === "failed" && (
                  <button className="small" onClick={() => reopen.mutate(r.id)}>
                    reopen
                  </button>
                )}
              </div>
            ))}
          </div>
        ),
      )}
      {rows.length === 0 && !requests.isLoading && <div className="muted">No requests yet — submit the first one above.</div>}
    </div>
  );
}
